import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Collaborators from '@/pages/Collaborators';
import CollaboratorDetail from '@/pages/CollaboratorDetail';
import Tools from '@/pages/Tools';
import ToolDetail from '@/pages/ToolDetail';
import Assignments from '@/pages/Assignments';
import History from '@/pages/History';
import Alerts from '@/pages/Alerts';
import Users from '@/pages/Users';
import Rentals from '@/pages/Rentals';
import ToolIssues from '@/pages/ToolIssues';
import ExportImport from '@/pages/ExportImport';
import ToolKits from '@/pages/ToolKits';
import Layout from '@/components/Layout';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import OfflineIndicator from '@/components/OfflineIndicator';
import ScannerProvider from '@/components/ScannerProvider';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/me`)
        .then(res => setUser(res.data))
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg font-medium text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  // Determine the default route based on user role
  const getDefaultRoute = () => {
    if (user?.role === 'client') return '/rentals';
    return '/';
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <ScannerProvider>
          <OfflineIndicator />
          <Routes>
            <Route path="/login" element={!user ? <Login /> : <Navigate to={getDefaultRoute()} />} />
            <Route element={user ? <Layout /> : <Navigate to="/login" />}>
              {/* Client only sees rentals */}
              {user?.role === 'client' ? (
                <>
                  <Route path="/rentals" element={<Rentals />} />
                  <Route path="*" element={<Navigate to="/rentals" />} />
                </>
              ) : (
                <>
                  <Route path="/" element={<Dashboard />} />
                  {/* Rentals page accessible to all users */}
                  <Route path="/rentals" element={<Rentals />} />
                  {/* Admin and Manager routes */}
                  {user?.role !== 'collaborator' && (
                    <>
                      <Route path="/collaborators" element={<Collaborators />} />
                      <Route path="/collaborators/:id" element={<CollaboratorDetail />} />
                      <Route path="/tools" element={<Tools />} />
                      <Route path="/tools/:id" element={<ToolDetail />} />
                      <Route path="/assignments" element={<Assignments />} />
                      <Route path="/history" element={<History />} />
                      <Route path="/alerts" element={<Alerts />} />
                      <Route path="/tool-issues" element={<ToolIssues />} />
                      <Route path="/export" element={<ExportImport />} />
                      <Route path="/kits" element={<ToolKits />} />
                    </>
                  )}
                  {/* Admin only routes */}
                  {user?.role === 'admin' && <Route path="/users" element={<Users />} />}
                  {/* Catch-all redirect */}
                  <Route path="*" element={<Navigate to="/" />} />
                </>
              )}
            </Route>
          </Routes>
        </ScannerProvider>
      </BrowserRouter>
      <PWAInstallPrompt />
      <Toaster />
    </AuthContext.Provider>
  );
}

export default App;